import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHeader from '../../components/common/PageHeader';
import { selectUser } from '../../services/reducers/user';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import { API_URL } from '../../config/api';
import { affiliatesV2API } from '../../services/api/v2/affiliates';
import { Drawer, Modal } from '../../theme/components';

const MyAffiliatesPage = () => {
  const user = useSelector(selectUser);
  const [activeTab, setActiveTab] = useState('approved');
  const [affiliates, setAffiliates] = useState([]);
  const [allAffiliates, setAllAffiliates] = useState([]); // Todos os afiliados para contadores
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    type: 'all' // 'all', 'direct', 'indirect', 'seller'
  });
  
  // Estados para drawer de edição
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [affiliateDetails, setAffiliateDetails] = useState(null);
  const [affiliateSales, setAffiliateSales] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [editForm, setEditForm] = useState({
    commission_rate: '',
    status: ''
  });
  
  // Estado para modal de sucesso
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Estados para modal de atribuição de produtos
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [sellers, setSellers] = useState([]); // Vendedores e Afiliados Diretos
  const [products, setProducts] = useState([]); // Produtos do produtor
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sellerLinkedProducts, setSellerLinkedProducts] = useState([]); // Produtos já vinculados ao seller selecionado
  const productsPerPage = 6;


  // Função para aplicar filtros (definida antes dos useEffect)
  const applyFilters = useCallback((allAffiliates) => {
    // Filtrar por status da aba
    const filteredByStatus = allAffiliates.filter(affiliate => 
      activeTab === 'approved' ? affiliate.status === 'approved' :
      activeTab === 'pending' ? affiliate.status === 'pending' :
      affiliate.status === 'rejected'
    );

    // Aplicar filtro de busca
    let filteredAffiliates = filteredByStatus;

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filteredAffiliates = filteredAffiliates.filter(affiliate =>
        affiliate.user_name?.toLowerCase().includes(searchLower) ||
        affiliate.user_email?.toLowerCase().includes(searchLower) ||
        affiliate.product_name?.toLowerCase().includes(searchLower)
      );
    }

    setAffiliates(filteredAffiliates);
    console.log(`📊 Filtros aplicados - Resultado final: ${filteredAffiliates.length} afiliados`);
  }, [activeTab, filters.search]);

  // Carregar todos os afiliados de uma vez
  useEffect(() => {
    loadAllAffiliates();
  }, []);

  // Aplicar filtros quando mudarem
  useEffect(() => {
    applyFilters(allAffiliates);
  }, [allAffiliates, applyFilters]);

  const loadAllAffiliates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Carregando afiliados...');
      
      // Migrado pra V2: GET /api/v2/affiliates/of-my-products
      // (substitui V1 /affiliation/affiliates que ficou 410 deprecated)
      const response = await affiliatesV2API.listOfMyProducts();
      const rows = response.data?.affiliates || [];

      // Mapeia pra shape esperado pela UI
      const affiliates = rows.map((row) => ({
        id: row.id,
        user_id: row.user_id,
        user_name: row.affiliate_name || '—',
        user_email: row.affiliate_email || '',
        product_id: row.product_id,
        product_name: row.product_name || '—',
        product_uuid: row.product_uuid || null,
        product_price: 0, // backend V2 não retorna preço aqui — UI pode esconder
        status: row.status || 'pending',
        commission_rate: row.commission_value_override
          ?? row.commission_rate
          ?? null,
        commission_type: row.commission_type || 'percentage',
        affiliation_type: row.affiliation_type || 'indirect',
        seller_exclusive_owner_id: null,
        manager_id: row.manager_id || null,
        manager_name: null,
        created_at: row.created_at,
      }));

      setAllAffiliates(affiliates);
    } catch (err) {
      console.error('❌ Erro na requisição:', err);
      setError(`Erro de rede: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };



  // Função para identificar tipo de afiliado (para badges)
  const getAffiliateType = (affiliate) => {
    // Vendedor: seller_exclusive_owner_id não é NULL e não é 0/undefined
    if (affiliate.seller_exclusive_owner_id !== null && affiliate.seller_exclusive_owner_id !== undefined && affiliate.seller_exclusive_owner_id !== 0) {
      return 'seller';
    }
    // Afiliado direto: affiliation_type === 'direct'
    if (affiliate.affiliation_type === 'direct') {
      return 'direct';
    }
    // Caso padrão: afiliado indireto
    return 'indirect';
  };

  // Função para obter badge do tipo
  const getTypeBadge = (affiliate) => {
    const type = getAffiliateType(affiliate);
    
    if (type === 'seller') {
      return <span className="badge bg-success text-white d-flex align-items-center gap-1" style={{ fontSize: '10px' }}>
        🟢 Vendedor
      </span>;
    }
    
    if (type === 'direct') {
      return <span className="badge bg-primary text-white d-flex align-items-center gap-1" style={{ fontSize: '10px' }}>
        🔵 Meu Afiliado
      </span>;
    }
    
    return <span className="badge bg-secondary text-white d-flex align-items-center gap-1" style={{ fontSize: '10px' }}>
      🟡 Normal
    </span>;
  };

  // Função para carregar dados completos do afiliado
  const loadAffiliateDetails = async (affiliate) => {
    try {
      setLoadingDetails(true);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      
      // Buscar dados completos
      const detailsResponse = await fetch(`${API_URL}/api/products/${affiliate.product_id}/affiliates/${affiliate.user_id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Buscar vendas
      const salesResponse = await fetch(`${API_URL}/api/products/${affiliate.product_id}/affiliates/${affiliate.user_id}/sales`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (detailsResponse.ok && salesResponse.ok) {
        const detailsData = await detailsResponse.json();
        const salesData = await salesResponse.json();
        
        setAffiliateDetails(detailsData.data);
        setAffiliateSales(salesData.data || []);
        
        // Preencher formulário de edição
        if (detailsData.data.affiliation) {
          setEditForm({
            commission_rate: detailsData.data.affiliation.commission_rate || affiliate.commission_rate,
            status: detailsData.data.affiliation.status || affiliate.status
          });
        } else {
          setEditForm({
            commission_rate: affiliate.commission_rate,
            status: affiliate.status
          });
        }
      }
      
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Função para abrir drawer de edição
  const handleOpenEditDrawer = async (affiliate) => {
    setEditingAffiliate(affiliate);
    setShowEditDrawer(true);
    await loadAffiliateDetails(affiliate);
  };

  // Função para editar afiliação
  const handleEditAffiliate = async () => {
    if (!editingAffiliate) return;
    
    try {
      setEditLoading(true);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/products/${editingAffiliate.product_id}/affiliates/${editingAffiliate.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          commission_rate: parseFloat(editForm.commission_rate),
          commission_type: 'percentage',
          status: editForm.status
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao atualizar afiliado');
      }

      // Mostrar modal de sucesso
      setSuccessMessage('Afiliado atualizado com sucesso!');
      setShowSuccessModal(true);
      
      // Recarregar lista
      await loadAllAffiliates();
      
      // Fechar drawer
      setShowEditDrawer(false);
      setEditingAffiliate(null);
      setAffiliateDetails(null);
      setAffiliateSales([]);
      
    } catch (err) {
      console.error('Erro ao editar afiliado:', err);
      setSuccessMessage('Erro ao atualizar afiliado: ' + err.message);
      setShowSuccessModal(true);
    } finally {
      setEditLoading(false);
    }
  };

  // Função para carregar vendedores e afiliados diretos + produtos
  const loadSellersAndProducts = async () => {
    try {
      setAssignLoading(true);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      
      console.log('🔍 Carregando sellers e produtos...');
      console.log('📍 API URL:', `${API_URL}/api/team/sellers`);
      
      // Buscar vendedores e afiliados diretos
      const sellersResponse = await fetch(`${API_URL}/api/team/sellers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📊 Resposta sellers:', sellersResponse.status, sellersResponse.ok);
      
      if (sellersResponse.ok) {
        const sellersData = await sellersResponse.json();
        console.log('✅ Dados sellers recebidos:', sellersData);
        console.log('👥 Quantidade de sellers:', sellersData.users?.length || 0);
        console.log('📋 Lista de sellers:', sellersData.users);
        setSellers(sellersData.users || []);
      } else {
        console.error('❌ Erro ao buscar sellers:', await sellersResponse.text());
      }
      
      // Buscar produtos do produtor
      console.log('🔍 Buscando produtos...');
      const productsResponse = await fetch(`${API_URL}/api/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📊 Resposta produtos:', productsResponse.status, productsResponse.ok);
      
      if (productsResponse.ok) {
        const productsData = await productsResponse.json();
        console.log('✅ Dados produtos recebidos:', productsData);
        console.log('📦 Estrutura da resposta:', Object.keys(productsData));
        
        // Tentar diferentes estruturas de resposta
        const productsList = productsData.products || productsData.data || productsData || [];
        console.log('📦 Produtos extraídos:', productsList.length, 'produtos');
        console.log('📋 Lista de produtos:', productsList);
        setProducts(productsList);
      } else {
        console.error('❌ Erro ao buscar produtos:', await productsResponse.text());
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setAssignLoading(false);
    }
  };
  
  // Função para atribuir produtos ao seller/afiliado
  const handleAssignProducts = async () => {
    if (!selectedSeller || selectedProducts.length === 0) {
      alert('Selecione um usuário e pelo menos um produto');
      return;
    }
    
    try {
      setAssignLoading(true);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/team/assign-products`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: selectedSeller.id,
          productIds: selectedProducts
        })
      });
      
      if (response.ok) {
        setSuccessMessage('Produtos atribuídos com sucesso!');
        setShowSuccessModal(true);
        setShowAssignModal(false);
        setSelectedSeller(null);
        setSelectedProducts([]);
        loadAllAffiliates(); // Recarregar lista
      } else {
        const errorData = await response.json();
        console.error('❌ Erro detalhado:', errorData);
        alert(errorData.message || errorData.error || 'Erro ao atribuir produtos');
      }
    } catch (error) {
      console.error('Erro ao atribuir produtos:', error);
      alert('Erro ao atribuir produtos');
    } finally {
      setAssignLoading(false);
    }
  };
  
  // Buscar produtos já vinculados ao seller selecionado
  useEffect(() => {
    if (!selectedSeller) {
      setSellerLinkedProducts([]);
      return;
    }

    // Usar a lista de afiliados já carregada para encontrar produtos vinculados
    const linkedProductIds = [];
    
    if (allAffiliates && allAffiliates.length > 0) {
      allAffiliates.forEach(affiliate => {
        // Verificar se o afiliado corresponde ao seller selecionado
        if (affiliate.user_id === selectedSeller.id && affiliate.product_id) {
          linkedProductIds.push(affiliate.product_id);
        }
      });
    }
    
    setSellerLinkedProducts(linkedProductIds);
    console.log('✅ Produtos já vinculados ao seller:', linkedProductIds);
  }, [selectedSeller, allAffiliates]);
  
  // Filtrar produtos: excluir os já vinculados e aplicar busca
  const filteredProducts = products.filter(product => {
    // Não mostrar produtos já vinculados ao seller selecionado
    if (selectedSeller && sellerLinkedProducts.includes(product.id)) {
      return false;
    }
    
    // Aplicar filtro de busca
    const matchesSearch = product.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
      product.product_code?.toLowerCase().includes(productSearch.toLowerCase());
    
    return matchesSearch;
  });
  
  // Paginação de produtos
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'approved': { color: 'success', icon: 'solar:check-circle-bold', text: 'Aprovado' },
      'pending': { color: 'warning', icon: 'solar:clock-circle-bold', text: 'Pendente' },
      'rejected': { color: 'danger', icon: 'solar:close-circle-bold', text: 'Rejeitado' }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    
    return (
      <span className={`badge bg-${config.color} text-white d-flex align-items-center gap-1`}>
        <Icon icon={config.icon} className="text-xs" />
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (priceInCents) => {
    const cents = parseInt(priceInCents || 0, 10);
    return CurrencyFormatter.centsToFormatted(cents);
  };

  // Estatísticas por aba (contagem de pessoas únicas)
  const getTabStats = (status) => {
    const tabAffiliates = allAffiliates.filter(a => a.status === status);
    return {
      count: new Set(tabAffiliates.map(a => a.user_id)).size,
      products: new Set(tabAffiliates.map(a => a.product_id)).size
    };
  };

  const approvedStats = getTabStats('approved');
  const pendingStats = getTabStats('pending');
  const rejectedStats = getTabStats('rejected');

  // Agrupar afiliados por pessoa (1 card por pessoa, sub-cards por produto)
  const groupedAffiliates = useMemo(() => {
    const grouped = {};
    affiliates.forEach(aff => {
      const key = aff.user_id;
      if (!grouped[key]) {
        grouped[key] = {
          user_id: aff.user_id,
          user_name: aff.user_name,
          user_email: aff.user_email,
          seller_exclusive_owner_id: aff.seller_exclusive_owner_id,
          products: []
        };
      }
      grouped[key].products.push(aff);
    });
    // Ordenar por nome
    return Object.values(grouped).sort((a, b) =>
      (a.user_name || '').localeCompare(b.user_name || '')
    );
  }, [affiliates]);

  return (
    <MasterLayout>
      <Breadcrumb 
        title="Meus Afiliados" 
        items={[
          { label: 'Início', path: '/Dashboard' },
          { label: 'Meus Afiliados', path: '/my-affiliates' }
        ]}
      />

      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
      <PageHeader
        icon="mdi:account-multiple"
        title="Meus Afiliados"
        subtitle="Gerencie todos os afiliados que promovem seus produtos"
      />
        
        <button
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={() => {
            setShowAssignModal(true);
            loadSellersAndProducts();
          }}
          style={{
            borderRadius: '8px',
            padding: '10px 20px',
            fontWeight: '500',
            fontSize: '14px',
            border: 'none',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
          }}
        >
          <Icon icon="mdi:link-variant-plus" style={{ fontSize: '20px' }} />
          Atribuir Afiliação
        </button>
      </div>

      <div className="dashboard-main-body">
        {/* Cards de Resumo - Padrão Dashboard */}
        <div className="row g-3" style={{ marginBottom: '24px' }}>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="flex-grow-1">
                    <h6 className="card-title text-muted mb-2" style={{ fontSize: '12px', fontWeight: '500' }}>Afiliados Ativos</h6>
                    <span className="mb-1 d-block" style={{ fontSize: '20px', fontWeight: 'bold',  }}>
                      {approvedStats.count}
                    </span>
                    <small className="text-success" style={{ fontSize: '11px', fontWeight: '500' }}>
                      {approvedStats.count === 1 ? 'Pessoa promovendo' : 'Pessoas promovendo'}
                    </small>
                  </div>
                  <div className="bg-success bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                    <Icon icon="mdi:account-check" className="text-success" style={{ fontSize: '20px' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="flex-grow-1">
                    <h6 className="card-title text-muted mb-2" style={{ fontSize: '12px', fontWeight: '500' }}>Solicitações</h6>
                    <span className="mb-1 d-block" style={{ fontSize: '20px', fontWeight: 'bold',  }}>
                      {pendingStats.count}
                    </span>
                    <small className="text-warning" style={{ fontSize: '11px', fontWeight: '500' }}>
                      Aguardando aprovação
                    </small>
                  </div>
                  <div className="bg-warning bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                    <Icon icon="mdi:clock-outline" className="text-warning" style={{ fontSize: '20px' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="flex-grow-1">
                    <h6 className="card-title text-muted mb-2" style={{ fontSize: '12px', fontWeight: '500' }}>Produtos</h6>
                    <span className="mb-1 d-block" style={{ fontSize: '20px', fontWeight: 'bold',  }}>
                      {new Set(allAffiliates.map(a => a.product_id)).size}
                    </span>
                    <small className="text-primary" style={{ fontSize: '11px', fontWeight: '500' }}>
                      Com afiliados
                    </small>
                  </div>
                  <div className="bg-primary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                    <Icon icon="mdi:package-variant" className="text-primary" style={{ fontSize: '20px' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="flex-grow-1">
                    <h6 className="card-title text-muted mb-2" style={{ fontSize: '12px', fontWeight: '500' }}>Comissão Média</h6>
                    <span className="mb-1 d-block" style={{ fontSize: '20px', fontWeight: 'bold',  }}>
                      {allAffiliates.length > 0 
                        ? `${(allAffiliates.reduce((sum, a) => sum + (parseFloat(a.commission_rate) || 0), 0) / allAffiliates.length).toFixed(1)}%`
                        : '0%'
                      }
                    </span>
                    <small className="text-info" style={{ fontSize: '11px', fontWeight: '500' }}>
                      Taxa média
                    </small>
                  </div>
                  <div className="bg-info bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                    <Icon icon="mdi:percent" className="text-info" style={{ fontSize: '20px' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="card radius-12 mb-24">
          <div className="card-body p-20">
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label text-sm fw-medium">Buscar</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nome, email ou produto..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
              
              <div className="col-md-4 d-flex align-items-end">
                <button
                  onClick={loadAllAffiliates}
                  className="btn btn-primary-600 text-white fw-medium w-100 icon-card"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Carregando...
                    </>
                  ) : (
                    <>
                      <Icon icon="solar:refresh-bold" className="me-2" />
                      Atualizar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Abas de Navegação */}
        <div className="card radius-12">
          <div className="card-header border-bottom bg-base py-16 px-24">
            <div className="d-flex align-items-center gap-3 flex-column flex-md-row justify-content-md-between">
              <span style={{ fontSize: '16px', fontWeight: '600', display: 'block' }}>Gestão de Afiliados</span>
              
              <nav className="d-flex gap-2 flex-wrap">
                <button
                  onClick={() => setActiveTab('approved')}
                  className={`icon-card btn btn-sm fw-medium ${
                    activeTab === 'approved' 
                      ? 'btn-success-600 text-white' 
                      : 'btn-outline-success-600 text-success-600'
                  }`}
                >
                  <Icon icon="solar:user-check-bold" className="me-1" />
                  Meus Afiliados ({approvedStats.count})
                </button>
                
                <button
                  onClick={() => setActiveTab('pending')}
                  className={`icon-card btn btn-sm fw-medium ${
                    activeTab === 'pending' 
                      ? 'btn-warning-600 text-white' 
                      : 'btn-outline-warning-600 text-warning-600'
                  }`}
                >
                  <Icon icon="solar:clock-circle-bold" className="me-1" />
                  Solicitações ({pendingStats.count})
                </button>

                <button
                  onClick={() => setActiveTab('rejected')}
                  className={`icon-card btn btn-sm fw-medium ${
                    activeTab === 'rejected' 
                      ? 'btn-danger-600 text-white' 
                      : 'btn-outline-danger-600 text-danger-600'
                  }`}
                >
                  <Icon icon="solar:close-circle-bold" className="me-1" />
                  Recusados ({rejectedStats.count})
                </button>
              </nav>
            </div>
          </div>
          
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" />
                <p className="text-muted">Carregando afiliados...</p>
              </div>
            ) : error ? (
              <div className="text-center py-5">
                <Icon icon="solar:close-circle-bold" className="text-danger mb-3" style={{ fontSize: '48px' }} />
                <h5 className="text-danger mb-2">Erro ao carregar afiliados</h5>
                <p className="text-muted mb-3">{error}</p>
                <button 
                  onClick={loadAllAffiliates}
                  className="btn btn-primary-600 text-white"
                >
                  Tentar Novamente
                </button>
              </div>
            ) : affiliates.length === 0 ? (
              <div className="text-center py-5">
                <Icon 
                  icon={activeTab === 'approved' ? "solar:user-check-bold" : 
                        activeTab === 'pending' ? "solar:clock-circle-bold" : "solar:close-circle-bold"} 
                  className="text-secondary-light mb-3" 
                  style={{ fontSize: '64px' }} 
                />
                <h5 className="text-secondary-light mb-2">
                  {activeTab === 'approved' ? 'Nenhum afiliado ativo' : 
                   activeTab === 'pending' ? 'Nenhuma solicitação pendente' : 'Nenhum afiliado recusado'}
                </h5>
                <p className="text-muted mb-3">
                  {activeTab === 'approved' 
                    ? 'Você ainda não possui afiliados aprovados.'
                    : activeTab === 'pending'
                    ? 'Você não possui solicitações de afiliação aguardando aprovação.'
                    : 'Você não possui afiliados recusados.'
                  }
                </p>
              </div>
            ) : (
              <div className="p-3">
                <div className="row g-3">
                  {groupedAffiliates.map((person) => {
                    const oldestDate = person.products.reduce((oldest, p) => {
                      if (!oldest || (p.created_at && new Date(p.created_at) < new Date(oldest))) return p.created_at;
                      return oldest;
                    }, null);

                    return (
                      <div key={person.user_id} className="col-12">
                        <div style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '20px',
                          background: '#fff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          transition: 'box-shadow 0.2s ease'
                        }}>
                          {/* Header da Pessoa */}
                          <div className="d-flex align-items-center gap-3 mb-3">
                            <div style={{
                              width: '44px', height: '44px', borderRadius: '50%',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#fff', fontWeight: '600', fontSize: '16px', flexShrink: 0
                            }}>
                              {(person.user_name || '?').charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="fw-semibold" style={{ fontSize: '14px' }}>
                                  {person.user_name}
                                </span>
                                {getTypeBadge(person.products[0])}
                              </div>
                              <span className="text-muted d-block" style={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {person.user_email}
                              </span>
                              <span className="text-muted" style={{ fontSize: '11px' }}>
                                Desde {formatDate(oldestDate)}
                              </span>
                            </div>
                            <span className="badge bg-light text-dark" style={{ fontSize: '11px', fontWeight: '500' }}>
                              {person.products.length} {person.products.length === 1 ? 'produto' : 'produtos'}
                            </span>
                          </div>

                          {/* Sub-cards de Produtos (scroll horizontal quando muitos) */}
                          <div className="d-flex gap-2" style={{ overflowX: 'auto', paddingBottom: '4px' }}>
                            {person.products.map((prod) => (
                              <div
                                key={`${prod.id}-${prod.product_id}`}
                                style={{
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  background: '#f9fafb',
                                  minWidth: '240px',
                                  maxWidth: '300px',
                                  flex: '0 0 auto',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                onClick={() => handleOpenEditDrawer(prod)}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = '#667eea';
                                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(102,126,234,0.15)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = '#e5e7eb';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                              >
                                <div className="d-flex align-items-start justify-content-between mb-2">
                                  <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
                                    <Icon icon="solar:box-bold" className="text-primary" style={{ fontSize: '16px', flexShrink: 0 }} />
                                    <span className="fw-medium" style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {prod.product_name}
                                    </span>
                                  </div>
                                  {getStatusBadge(prod.status)}
                                </div>
                                <div className="d-flex align-items-center justify-content-between">
                                  <div>
                                    <span className="fw-semibold text-warning-600" style={{ fontSize: '13px' }}>
                                      {prod.commission_rate}%
                                    </span>
                                    <span className="text-muted ms-1" style={{ fontSize: '11px' }}>
                                      ({formatPrice(Math.round((parseInt(prod.product_price || 0, 10) * (parseFloat(prod.commission_rate) || 0)) / 100))}/venda)
                                    </span>
                                  </div>
                                  <Icon icon="solar:eye-bold" className="text-primary" style={{ fontSize: '16px', opacity: 0.6 }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Drawer de Edição de Afiliação */}
      <Drawer
        show={showEditDrawer}
        onHide={() => {
          setShowEditDrawer(false);
          setEditingAffiliate(null);
          setAffiliateDetails(null);
          setAffiliateSales([]);
        }}
        title={editingAffiliate ? `Editar Afiliação - ${editingAffiliate.user_name}` : 'Editar Afiliação'}
        icon="mdi:account-edit"
        size="lg"
        footer={
          <>
            <Drawer.Button 
              variant="secondary" 
              onClick={() => {
                setShowEditDrawer(false);
                setEditingAffiliate(null);
                setAffiliateDetails(null);
                setAffiliateSales([]);
              }}
            >
              Cancelar
            </Drawer.Button>
            <Drawer.Button 
              variant="warning"
              icon="mdi:content-save"
              onClick={handleEditAffiliate}
              loading={editLoading}
              disabled={!editForm.commission_rate || !editForm.status}
            >
              Salvar Alterações
            </Drawer.Button>
          </>
        }
      >
        {loadingDetails ? (
          <div className="text-center py-5">
            <div className="spinner-border text-warning mb-3" />
            <p className="text-muted">Carregando detalhes...</p>
          </div>
        ) : editingAffiliate && affiliateDetails ? (
          <>
            {/* 1. DADOS DO AFILIADO */}
            <Drawer.Section title="Dados do Afiliado" icon="mdi:account">
              <Drawer.Row 
                label="Nome Completo" 
                value={affiliateDetails.user.name || 'Não informado'}
              />
              <Drawer.Row 
                label="Email" 
                value={affiliateDetails.user.email || 'Não informado'}
              />
              <Drawer.Row 
                label="Telefone" 
                value={affiliateDetails.user.phone || 'Não informado'}
              />
              <Drawer.Row 
                label="Status da Conta" 
                value={affiliateDetails.user.status || 'Não informado'}
              />
              
              <div className="mt-3">
                {getTypeBadge(editingAffiliate)}
              </div>
              
              {affiliateDetails.affiliation?.manager_name && (
                <Drawer.InfoCard variant="info" className="mt-3">
                  <Icon icon="mdi:information" className="me-2" />
                  Este afiliado pertence ao gerente: <strong>{affiliateDetails.affiliation.manager_name}</strong>
                </Drawer.InfoCard>
              )}
            </Drawer.Section>

            <Drawer.Divider />

            {/* 2. DADOS DO RESPONSÁVEL (se existir) */}
            {affiliateDetails.responsible && (
              <>
                <Drawer.Section title="Dados do Responsável" icon="mdi:account-tie">
                  {affiliateDetails.responsible.admin_name && (
                    <Drawer.Row 
                      label="Nome do Administrador" 
                      value={affiliateDetails.responsible.admin_name}
                    />
                  )}
                  
                  {affiliateDetails.responsible.admin_cpf && (
                    <Drawer.Row 
                      label="CPF do Administrador" 
                      value={affiliateDetails.responsible.admin_cpf}
                    />
                  )}
                  
                  {(affiliateDetails.responsible.address_street || affiliateDetails.responsible.address_city) && (
                    <>
                      <Drawer.Divider />
                      <h6 className="fw-bold mb-3 mt-3">
                        <Icon icon="mdi:map-marker" className="me-2" />
                        Endereço
                        </h6>
                        
                      {affiliateDetails.responsible.address_cep && (
                        <Drawer.Row 
                          label="CEP" 
                          value={affiliateDetails.responsible.address_cep}
                        />
                      )}
                      
                      {affiliateDetails.responsible.address_street && (
                        <Drawer.Row 
                          label="Logradouro" 
                          value={`${affiliateDetails.responsible.address_street}${affiliateDetails.responsible.address_number ? ', ' + affiliateDetails.responsible.address_number : ''}${affiliateDetails.responsible.address_complement ? ' - ' + affiliateDetails.responsible.address_complement : ''}`}
                        />
                      )}
                      
                      {affiliateDetails.responsible.address_neighborhood && (
                        <Drawer.Row 
                          label="Bairro" 
                          value={affiliateDetails.responsible.address_neighborhood}
                        />
                      )}
                      
                      {affiliateDetails.responsible.address_city && (
                        <Drawer.Row 
                          label="Cidade/Estado" 
                          value={`${affiliateDetails.responsible.address_city}${affiliateDetails.responsible.address_state ? '/' + affiliateDetails.responsible.address_state : ''}`}
                        />
                      )}
                    </>
                  )}
                </Drawer.Section>

                <Drawer.Divider />
              </>
            )}

            {!affiliateDetails.responsible && (
              <>
                <Drawer.Section title="Dados Cadastrais" icon="mdi:account-alert">
                  <Drawer.EmptyState 
                    icon="mdi:account-alert"
                    text="Dados cadastrais ainda não foram preenchidos"
                  />
                </Drawer.Section>

                <Drawer.Divider />
              </>
            )}

            {/* 2. DADOS DO PRODUTO */}
            <Drawer.Section title="Dados do Produto" icon="mdi:package-variant">
              <Drawer.Row 
                label="Nome do Produto" 
                value={editingAffiliate.product_name}
              />
              <Drawer.Row 
                label="Preço" 
                value={formatPrice(editingAffiliate.product_price)}
                highlight
              />
              <Drawer.Row 
                label="Produto ID" 
                value={editingAffiliate.product_id}
              />
            </Drawer.Section>

            <Drawer.Divider />

            {/* 3. EDIÇÃO DE AFILIAÇÃO */}
            <div 
              className="bg-base-50"
              style={{
                position: 'relative',
                borderTop: '4px solid #667eea',
                borderRadius: '12px',
                padding: '24px',
                marginTop: '8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}
            >
              <div className="mb-4">
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  <Icon icon="mdi:pencil" style={{ fontSize: '18px', color: '#667eea' }} />
                  <span style={{ fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Editar Afiliação
                  </span>
                </div>
              </div>
                        
              {/* Campo de Comissão */}
              <div className="mb-4">
                <label className="form-label fw-medium mb-2">
                  <Icon icon="mdi:percent" className="me-2" style={{ color: '#667eea' }} />
                  Comissão (%)
                </label>
                <input
                  type="number"
                  className="form-control bg-base"
                  min="0"
                  max="100"
                  step="0.1"
                  value={editForm.commission_rate || ''}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    commission_rate: e.target.value
                  })}
                  style={{
                    border: '2px solid var(--neutral-300)',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    fontSize: '15px',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#52525b';
                    e.target.style.boxShadow = '0 0 0 3px rgba(82, 82, 91, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--neutral-300)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                        </div>
                        
              {/* Status em formato de cards */}
              <div>
                <label className="form-label fw-medium mb-3">
                  <Icon icon="mdi:check-circle" className="me-2" style={{ color: '#667eea' }} />
                  Status
                </label>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(3, 1fr)', 
                  gap: '12px'
                }}>
                  {[
                    { 
                      value: 'pending', 
                      label: 'Pendente', 
                      icon: 'solar:clock-circle-bold',
                      color: '#F59E0B',
                      bgColor: '#F59E0B',
                      shadowColor: 'rgba(245, 158, 11, 0.3)'
                    },
                    { 
                      value: 'approved', 
                      label: 'Aprovado', 
                      icon: 'solar:check-circle-bold',
                      color: '#10B981',
                      bgColor: '#10B981',
                      shadowColor: 'rgba(16, 185, 129, 0.3)'
                    },
                    { 
                      value: 'rejected', 
                      label: 'Rejeitado', 
                      icon: 'solar:close-circle-bold',
                      color: '#EF4444',
                      bgColor: '#EF4444',
                      shadowColor: 'rgba(239, 68, 68, 0.3)'
                    }
                  ].map((status) => (
                    <div
                      key={status.value}
                      className="bg-base"
                      onClick={() => setEditForm({
                        ...editForm,
                        status: status.value
                      })}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '18px 12px',
                        border: editForm.status === status.value 
                          ? `2px solid ${status.color}` 
                          : '2px solid var(--neutral-300)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        background: editForm.status === status.value
                          ? `${status.bgColor}20`
                          : undefined,
                        boxShadow: editForm.status === status.value
                          ? `0 4px 12px ${status.shadowColor}`
                          : 'none',
                        transform: editForm.status === status.value ? 'translateY(-2px)' : 'none',
                        minHeight: '100px'
                      }}
                      onMouseEnter={(e) => {
                        if (editForm.status !== status.value) {
                          e.currentTarget.style.borderColor = status.color;
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (editForm.status !== status.value) {
                          e.currentTarget.style.borderColor = 'var(--neutral-300)';
                          e.currentTarget.style.transform = 'none';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      <Icon 
                        icon={status.icon} 
                        style={{ 
                          fontSize: '32px', 
                          color: editForm.status === status.value ? status.color : '#71717a',
                          transition: 'color 0.2s ease'
                        }} 
                      />
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: editForm.status === status.value ? '600' : '500',
                        color: editForm.status === status.value ? status.color : undefined,
                        textAlign: 'center',
                        transition: 'all 0.2s ease'
                      }}>
                        {status.label}
                      </span>
                          </div>
                  ))}
                      </div>
                    </div>
            </div>
                  
            <Drawer.Divider />

            {/* 4. VENDAS REALIZADAS */}
            <Drawer.Section title="Vendas Realizadas" icon="mdi:chart-line">
              {affiliateSales.length > 0 ? (
                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table table-sm">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{ fontSize: '11px' }}>ID</th>
                        <th style={{ fontSize: '11px' }}>Cliente</th>
                        <th style={{ fontSize: '11px' }}>Valor</th>
                        <th style={{ fontSize: '11px' }}>Comissão</th>
                        <th style={{ fontSize: '11px' }}>Status</th>
                        <th style={{ fontSize: '11px' }}>Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {affiliateSales.map((sale) => (
                        <tr key={sale.id}>
                          <td style={{ fontSize: '12px' }}>#{sale.id}</td>
                          <td style={{ fontSize: '12px' }}>
                            {sale.customer_name || sale.customer_email || 'N/A'}
                          </td>
                          <td style={{ fontSize: '12px', fontWeight: '600' }}>
                            {formatPrice(sale.total_amount)}
                          </td>
                          <td style={{ fontSize: '12px', color: '#10B981', fontWeight: '600' }}>
                            {formatPrice(sale.commission_amount || 0)}
                            <br />
                            <small style={{ color: '#6B7280', fontSize: '10px' }}>
                              ({sale.commission_rate}%)
                            </small>
                          </td>
                          <td style={{ fontSize: '12px' }}>
                            <span className={`badge bg-${sale.is_paid ? 'success' : 'warning'}`}>
                              {sale.is_paid ? 'Pago' : 'Pendente'}
                            </span>
                          </td>
                          <td style={{ fontSize: '12px' }}>
                            {formatDateTime(sale.sale_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                        </div>
              ) : (
                <Drawer.EmptyState 
                  icon="mdi:chart-line-variant"
                  text="Este afiliado ainda não realizou vendas neste produto"
                />
              )}
              
              {affiliateSales.length > 0 && (
                <div className="mt-3 p-3" style={{ background: '#F9FAFB', borderRadius: '8px' }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-medium">Total de Vendas:</span>
                    <strong>{affiliateSales.length}</strong>
                        </div>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <span className="fw-medium">Total em Comissões:</span>
                    <strong style={{ color: '#10B981' }}>
                      {formatPrice(affiliateSales.reduce((sum, sale) => sum + (parseInt(sale.commission_amount || 0, 10)), 0))}
                    </strong>
            </div>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <span className="fw-medium">Comissões Pagas:</span>
                    <strong style={{ color: '#059669' }}>
                      {formatPrice(affiliateSales.filter(s => s.is_paid).reduce((sum, sale) => sum + (parseInt(sale.commission_amount || 0, 10)), 0))}
                    </strong>
          </div>
        </div>
      )}
            </Drawer.Section>
          </>
        ) : (
          <Drawer.EmptyState 
            icon="mdi:alert"
            text="Erro ao carregar detalhes do afiliado"
          />
        )}
      </Drawer>

      {/* Modal de Sucesso */}
      <Modal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        title={successMessage.includes('Erro') ? 'Erro' : 'Sucesso!'}
        icon={successMessage.includes('Erro') ? 'mdi:alert-circle' : 'solar:check-circle-bold'}
        size="md"
        footer={
          <Modal.Button 
            variant={successMessage.includes('Erro') ? 'danger' : 'success'}
            onClick={() => setShowSuccessModal(false)}
            icon="mdi:check"
            fullWidth
          >
            Entendi
          </Modal.Button>
        }
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Icon 
            icon={successMessage.includes('Erro') ? 'solar:danger-triangle-bold' : 'solar:check-circle-bold'}
            style={{ 
              fontSize: '64px',
              color: successMessage.includes('Erro') ? '#EF4444' : '#10B981',
              marginBottom: '16px'
            }}
          />
          <p style={{ 
            fontSize: '16px', 
            fontWeight: '500',
            color: '#374151',
            marginBottom: '8px'
          }}>
            {successMessage.includes('Erro') ? 'Ops! Algo deu errado' : 'Operação realizada com sucesso!'}
          </p>
          <p style={{ 
            fontSize: '14px', 
            color: '#6B7280',
            margin: 0
          }}>
            {successMessage}
                          </p>
                        </div>
      </Modal>

      {/* Modal de Atribuição de Produtos */}
      {showAssignModal && (
        <Modal
          show={showAssignModal}
          onHide={() => {
            setShowAssignModal(false);
            setSelectedSeller(null);
            setSelectedProducts([]);
            setProductSearch('');
            setCurrentPage(1);
          }}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedSeller(null);
            setSelectedProducts([]);
            setProductSearch('');
            setCurrentPage(1);
          }}
          title="Atribuir Afiliação"
          subtitle="Selecione um usuário e os produtos para vincular"
          size="xl"
        >
        <div style={{ padding: '24px' }}>
          {assignLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
                        </div>
                      </div>
          ) : (
            <>
              {/* Selecionar Usuário */}
              <div className="mb-4">
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151',
                  marginBottom: '12px',
                  display: 'block'
                }}>
                  Selecionar Usuário
                </label>
                <select
                  className="form-select"
                  value={selectedSeller?.id || ''}
                  onChange={(e) => {
                    const seller = sellers.find(s => s.id === parseInt(e.target.value));
                    setSelectedSeller(seller);
                    setSelectedProducts([]); // Limpar seleção de produtos ao mudar o seller
                    setCurrentPage(1); // Voltar para primeira página
                  }}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    padding: '12px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Selecione um vendedor ou afiliado direto</option>
                  {sellers.map(seller => (
                    <option key={seller.id} value={seller.id}>
                      {seller.name} ({seller.email}) - {seller.user_type === 'seller' ? 'Vendedor' : 'Afiliado Direto'}
                    </option>
                  ))}
                </select>
                    </div>

              {/* Busca de Produtos */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151',
                  margin: 0
                }}>
                  Produtos Vinculados
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar produto..."
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    width: '250px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    padding: '8px 12px',
                    fontSize: '13px'
                  }}
                />
                </div>

              {/* Lista de Produtos (Grid 2x3) */}
              {!selectedSeller ? (
                <div className="text-center py-5">
                  <Icon icon="mdi:account-alert" style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '12px' }} />
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                    Selecione um usuário para ver os produtos disponíveis
                          </p>
                        </div>
              ) : (
                <div className="row g-4 mb-4">
                  {currentProducts.length > 0 ? (
                    currentProducts.map(product => (
                    <div key={product.id} className="col-md-4">
                      <div
                      onClick={() => {
                          setSelectedProducts(prev => 
                            prev.includes(product.id)
                              ? prev.filter(id => id !== product.id)
                              : [...prev, product.id]
                          );
                        }}
                        style={{
                          border: `2px solid ${selectedProducts.includes(product.id) ? '#667eea' : '#e5e7eb'}`,
                          borderRadius: '12px',
                          padding: '16px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          background: selectedProducts.includes(product.id) ? '#f5f3ff' : '#fff',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                        onMouseEnter={(e) => {
                          if (!selectedProducts.includes(product.id)) {
                            e.currentTarget.style.borderColor = '#cbd5e1';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!selectedProducts.includes(product.id)) {
                            e.currentTarget.style.borderColor = '#e5e7eb';
                            e.currentTarget.style.transform = 'none';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <span style={{ 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: '#1f2937',
                            flex: 1
                          }}>
                            {product.name}
                          </span>
                          {selectedProducts.includes(product.id) && (
                            <Icon 
                              icon="mdi:check-circle" 
                              style={{ 
                                fontSize: '20px', 
                                color: '#667eea',
                                flexShrink: 0,
                                marginLeft: '8px'
                              }} 
                            />
                          )}
                      </div>
                        <span style={{ 
                          fontSize: '12px', 
                          color: '#6b7280',
                          marginTop: 'auto'
                        }}>
                          Código: {product.product_code || 'N/A'}
                        </span>
                    </div>
                  </div>
                  ))
                ) : (
                  <div className="col-12">
                    <div className="text-center py-5">
                      <Icon icon="mdi:package-variant" style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '12px' }} />
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                        {selectedSeller ? 'Este usuário já está vinculado a todos os produtos disponíveis' : 'Nenhum produto encontrado'}
                      </p>
                </div>
                  </div>
                )}
              </div>
              )}

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center gap-2 mb-4" style={{ marginTop: '24px' }}>
                    <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    style={{ borderRadius: '6px' }}
                  >
                    <Icon icon="mdi:chevron-left" />
                    </button>
                  <span style={{ 
                    padding: '6px 12px', 
                    fontSize: '13px',
                    color: '#6b7280'
                  }}>
                    {currentPage} de {totalPages}
                  </span>
                    <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    style={{ borderRadius: '6px' }}
                  >
                    <Icon icon="mdi:chevron-right" />
                    </button>
                  </div>
                )}

              {/* Resumo da Seleção */}
              {selectedSeller && selectedProducts.length > 0 && (
                <div style={{
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '16px'
                }}>
                  <div className="d-flex align-items-center gap-2">
                    <Icon icon="mdi:information" style={{ fontSize: '18px', color: '#22c55e' }} />
                    <span style={{ fontSize: '13px', color: '#166534', fontWeight: '500' }}>
                      {selectedProducts.length} produto(s) serão vinculados a {selectedSeller.name}
                    </span>
              </div>
                </div>
              )}
              
              {/* Botões de Ação */}
              <div className="d-flex gap-2 justify-content-end">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedSeller(null);
                    setSelectedProducts([]);
                    setProductSearch('');
                    setCurrentPage(1);
                  }}
                  style={{
                    borderRadius: '8px',
                    padding: '10px 24px',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleAssignProducts}
                  disabled={!selectedSeller || selectedProducts.length === 0}
                  style={{
                    borderRadius: '8px',
                    padding: '10px 24px',
                    fontWeight: '500',
                    fontSize: '14px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none'
                  }}
                >
                  Atribuir Produtos
                </button>
              </div>
            </>
          )}
            </div>
        </Modal>
      )}

    </MasterLayout>
  );
};

export default MyAffiliatesPage;